import { useState } from 'react'

import { Modal } from '@/components/Modal'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { GoogleLogin } from '@/components/GoogleLogin'
import { IconContainer } from '@/components/IconContainer'
import { Avatar } from '@/components/Avatar'
import { DropdownMenu } from '@/components/DropdownMenu'
import { Pill } from '@/components/Pill'
import { Spinner } from '@/components/Spinner'
import { More } from '@/components/More'
import { useMe } from '@/hooks/useMe'
import { useUnlinkAccount } from '@/hooks/useUnlinkAccount'
import { useToast } from '@/hooks/Toast'
import { GoogleAccount, User } from '@/types'

import s from './Account.module.css'

type AccountExtended = GoogleAccount & {
  user: User
}

export const Account = () => {
  const { data } = useMe()
  const [unlinkingAccount, setUnlinkingAccount] = useState<User | null>(null)
  const unlinkAccountMutation = useUnlinkAccount()
  const toast = useToast()

  const unlinkAccount = async () => {
    if (!unlinkingAccount) return
    try {
      await unlinkAccountMutation.mutateAsync({
        accountUserId: unlinkingAccount.id,
        primaryUserId: unlinkingAccount.primaryUserId!,
      })
      setUnlinkingAccount(null)
    } catch (error) {
      toast.error('Something went wrong. Try again?')
    }
  }

  if (!data || !(data as { accounts: AccountExtended[] }).accounts) {
    return <Spinner />
  }

  const accounts = (data as { accounts: AccountExtended[] }).accounts
  const hasPersonalAccount = accounts.some((a) => !a.hd)
  const hasWorkAccount = accounts.some((a) => !!a.hd)

  return (
    <div>
      <p className={s.description}>
        Manage your linked accounts here. You can link your personal and work
        accounts. This allows you to login with either of your emails and all
        your links will be accessible to you at one place.
      </p>
      <div className={s.items}>
        {accounts?.map((account) => (
          <Card key={account.user.id}>
            <div className={s.item}>
              <IconContainer size="lg">
                <Avatar src={account.user.image} name={account.user.name} />
              </IconContainer>
              <div className={s.text}>
                <div>
                  <b>{account.user.email}</b>
                  {account.user.primaryUserId === account.user.id && (
                    <span className={s.label}>Primary</span>
                  )}
                </div>
                <div className={s.accountDescription}>
                  {account.hd
                    ? 'Used for all work related links'
                    : 'Used  for all your personal links'}
                </div>
                <Pill type={account.hd ? 'organization' : 'personal'} />
              </div>

              {accounts.length > 1 &&
                account.user.primaryUserId !== account.user.id && (
                  <DropdownMenu>
                    <DropdownMenu.Trigger>
                      {' '}
                      <More />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                      <DropdownMenu.Item
                        onClick={() => setUnlinkingAccount(account.user)}
                        destructive
                      >
                        Unlink
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu>
                )}
            </div>
          </Card>
        ))}
      </div>
      {(!hasPersonalAccount || !hasWorkAccount) && (
        <GoogleLogin ref="link_account_page" isLinkingAccount>
          {!hasPersonalAccount
            ? 'Link personal account'
            : hasWorkAccount
            ? 'Link another work account'
            : 'Link work account'}
        </GoogleLogin>
      )}

      <Modal
        title="Unlink Account"
        description={`Your personal and work links will split within respective accounts. Are you sure you want to unlink "${unlinkingAccount?.email}"?`}
        open={!!unlinkingAccount}
        onClose={() => setUnlinkingAccount(null)}
      >
        <Button
          variant="destructive"
          disabled={unlinkAccountMutation.isPending}
          onClick={unlinkAccount}
        >
          Yes, Unlink
        </Button>
      </Modal>
    </div>
  )
}
